import {
  ArrowRight,
  Gauge,
  LockKeyhole,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ProductWordmark from "@/components/ProductWordmark";
import DeviceStory, { DEVICE_STORY_LINE } from "@/components/DeviceStory";
import {
  HALO_WEBSITE_LIST_PRICE,
  allHaloStoresLive,
  haloStorefront,
  isStoreLive,
  storeStatusLabel,
} from "@/config/haloStorefront";
import {
  HALO_CUSTOMER_PROMISE,
  HALO_HERO_STRAPLINE,
  HALO_SOFTWARE_BENCHMARK,
} from "@/config/haloMarketing";
import { haloEducationArticles } from "@/config/haloEducation";
import { captureHaloCampaignEvent } from "@/lib/haloCampaignTracking";

const benefits = [
  {
    icon: ShieldCheck,
    eyebrow: "Stronger protection",
    title: "Protection, elevated.",
    body: "Halo SuperVPN brings VPN privacy, LightningFlow’s QuantumCloak security and encrypted traffic together in one remarkably simple experience.",
  },
  {
    icon: Gauge,
    eyebrow: "Faster encrypted traffic",
    title: "Fast by design.",
    body: "Halo SuperVPN delivered up to 25% faster performance—231 Mbps compared with 184 Mbps under the same conditions during comprehensive testing. Performance varies by device, network and location.",
  },
  {
    icon: Users,
    eyebrow: "Simple across devices",
    title: "One account. Three devices.",
    body: `Protect up to ${haloStorefront.device_limit} active supported devices with one Halo SuperVPN subscription.`,
  },
];

const faq = [
  [
    "Where can I get Halo SuperVPN?",
    "Halo SuperVPN is coming to Microsoft Store, Apple App Store, Mac App Store and Google Play. Choose your Store below to see current availability.",
  ],
  [
    "Does one subscription cover multiple devices?",
    `Yes. One Halo SuperVPN subscription protects up to ${haloStorefront.device_limit} active supported devices. The Store where you subscribe manages renewal, cancellation and refunds.`,
  ],
  [
    "Which app do I install on Mac and iPhone?",
    "On iPhone, install Halo SuperVPN. On Mac, use Halo SuperVPN on its own, or Fortress for Halo SuperVPN and SecureShield in one Mac app. Both use the same Halo subscription.",
  ],
  [
    "How does the trial work?",
    haloStorefront.trial_days
      ? `Your Store will confirm whether you are eligible for an introductory offer and show the renewal price and terms before you subscribe.`
      : "Your Store will show any introductory offer, renewal price and terms before you subscribe.",
  ],
  [
    "What does Halo SuperVPN log?",
    "Halo SuperVPN does not keep your browsing destinations, DNS-query history or traffic content as activity records. It keeps only the account, billing, security and service-health information needed to run and protect the service.",
  ],
  [
    "What does “up to 25% faster” mean?",
    "Halo SuperVPN delivered up to 25% faster performance—231 Mbps compared with 184 Mbps under the same conditions during comprehensive testing. Performance varies by device, network and location. See lightningflow.ai/evidence for the measured method.",
  ],
];

function StoreAction({ store, dark = false }) {
  const live = isStoreLive(store);
  const content = (
    <>
      <span className="font-mono text-[9px] uppercase tracking-[.2em] opacity-70">
        {live ? "Available from" : "Launch availability"}
      </span>
      <span className="mt-2 block font-display text-xl font-bold">{store.name}</span>
      <span className="mt-2 block text-sm opacity-75">{storeStatusLabel(store)}</span>
    </>
  );

  if (!live) {
    return (
      <div
        className={`h-full border px-5 py-6 text-left ${
          dark
            ? "border-white/20 bg-white/[.05] text-white"
            : "border-ink-100 bg-white text-ink-700"
        }`}
        aria-label={`${store.name}: ${storeStatusLabel(store)}`}
        aria-disabled="true"
      >
        {content}
      </div>
    );
  }

  return (
    <a
      className={`block h-full border px-5 py-6 text-left transition-colors ${
        dark
          ? "border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
          : "border-ink-900 bg-ink-900 text-white hover:border-electric hover:bg-electric"
      }`}
      href={store.url}
      target="_blank"
      rel="noreferrer"
      data-testid={`halo-store-${store.id}`}
      onClick={() => captureHaloCampaignEvent("HaloStoreClick", {
        campaign: "halo_product",
        store_id: store.id,
        platform: store.platform,
      })}
    >
      {content}
    </a>
  );
}

function SectionHeading({ eyebrow, title, body, light = false }) {
  return (
    <div className="max-w-3xl">
      <div className={`lf-eyebrow ${light ? "text-cyan-200" : ""}`}>{eyebrow}</div>
      <h2 className="mt-4 font-display text-4xl font-black leading-[1.02] md:text-5xl">
        {title}
      </h2>
      {body ? (
        <p className={`mt-5 text-lg leading-relaxed ${light ? "text-slate-200" : "text-ink-600"}`}>
          {body}
        </p>
      ) : null}
    </div>
  );
}

function SpeedComparison() {
  const { baseline, optimised } = HALO_SOFTWARE_BENCHMARK;
  const maxThroughput = Math.max(
    baseline.throughput_mbps,
    optimised.throughput_mbps,
  );
  const rows = [
    { ...baseline, accent: false },
    { ...optimised, accent: true },
  ];

  return (
    <div
      className="border border-ink-900 bg-white"
      role="group"
      aria-labelledby="halo-speed-chart-title"
    >
      <div className="grid gap-5 border-b border-ink-900 px-5 py-6 md:grid-cols-[1fr_auto] md:items-end md:px-7">
        <div>
          <p className="lf-eyebrow">LightningFlow performance test</p>
          <h3 id="halo-speed-chart-title" className="mt-2 font-display text-2xl font-black">
            {HALO_SOFTWARE_BENCHMARK.title}
          </h3>
        </div>
        <strong className="font-display text-3xl font-black text-electric md:text-4xl">
          {HALO_SOFTWARE_BENCHMARK.claim}
        </strong>
      </div>
      <ol className="divide-y divide-ink-100 px-5 py-2 md:px-7">
        {rows.map((item) => {
          const width = `${(item.throughput_mbps / maxThroughput) * 100}%`;
          return (
            <li
              key={item.label}
              className="grid gap-3 py-5 md:grid-cols-[minmax(190px,.7fr)_minmax(260px,1.6fr)_120px] md:items-center"
            >
              <span className="text-sm font-semibold text-ink-800">{item.label}</span>
              <div className="h-8 border-l border-ink-300 bg-ink-50" aria-hidden="true">
                <div
                  className={`h-full border-r border-ink-900 ${item.accent ? "bg-electric" : "bg-ink-300"}`}
                  style={{ width }}
                />
              </div>
              <strong className={`font-display text-2xl ${item.accent ? "text-electric" : "text-ink-700"}`}>
                {Math.round(item.throughput_mbps)}{" "}
                <span className="font-sans text-xs font-bold text-ink-500">Mbps</span>
              </strong>
            </li>
          );
        })}
      </ol>
      <p className="border-t border-ink-100 px-5 py-4 text-sm leading-relaxed text-ink-600 md:px-7">
        {HALO_SOFTWARE_BENCHMARK.test_note}
      </p>
    </div>
  );
}

function TrustStrip() {
  const commercialTerms = haloStorefront.pricing_published
    ? `Store-confirmed introductory offer · ${haloStorefront.monthly_price} monthly · ${haloStorefront.annual_price} annually`
    : "Store pricing, trial eligibility and renewal terms shown before purchase";

  const items = [
    ["Launch availability", "Check each Store below for a verified release"],
    ["One LightningFlow Account", `Up to ${haloStorefront.device_limit} active supported devices`],
    ["Store terms", commercialTerms],
  ];

  return (
    <section aria-label="Halo SuperVPN availability and subscription summary" className="border-y border-ink-100 bg-white">
      <div className="lf-container grid gap-px bg-ink-100 md:grid-cols-3">
        {items.map(([label, value]) => (
          <div key={label} className="bg-white px-6 py-6">
            <div className="font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{label}</div>
            <div className="mt-2 text-sm font-semibold leading-relaxed text-ink-900">{value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function HaloProductPage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: haloStorefront.product_name,
    applicationCategory: "SecurityApplication",
    operatingSystem: "Windows, macOS, iOS, Android",
    description: "Halo SuperVPN is LightningFlow's first-party private connectivity experience for Windows, macOS, iOS and Android.",
    ...(haloStorefront.pricing_published
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: haloStorefront.default_currency,
            price: haloStorefront.monthly_price.replace(/[^0-9.]/g, ""),
            availability: allHaloStoresLive
              ? "https://schema.org/InStock"
              : "https://schema.org/PreOrder",
          },
        }
      : {}),
  };

  return (
    <div className="bg-white text-ink-900">
      <SEO
        title="Halo SuperVPN — More Secure. Up to 25% Faster."
        description={HALO_CUSTOMER_PROMISE.description}
        canonicalPath="/halo"
        image="/halo/official-halo-wordmark.png"
        structuredData={structuredData}
      />

      <section className="lf-product-hero relative overflow-hidden bg-[#031124] text-white">
        <div className="absolute inset-0 lf-grid-bg-faint opacity-20" />
        <div className="lf-container relative grid gap-12 py-20 md:py-28 lg:max-w-5xl">
          <div>
            <ProductWordmark
              product="halo"
              size="hero"
              loading="eager"
              testId="halo-hero-wordmark"
            />
            <div className="lf-eyebrow text-cyan-200">{HALO_HERO_STRAPLINE}</div>
            <h1 className="mt-5 max-w-3xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">
              More secure.{" "}
              <span className="text-cyan-200">Up to 25% faster.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
              {HALO_CUSTOMER_PROMISE.description}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
                href="#plans"
                data-testid="halo-cta-get-halo"
              >
                See launch availability <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                className="lf-btn border border-white/40 text-white hover:border-white"
                to="/quantumcloak"
                data-testid="halo-cta-discover-quantumcloak"
              >
                Discover QuantumCloak
              </Link>
              <Link
                className="lf-btn border border-white/40 text-white hover:border-white"
                to="/account"
                data-testid="halo-cta-account"
              >
                LightningFlow Account
              </Link>
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />

      <section className="lf-editorial-section bg-[radial-gradient(circle_at_85%_15%,rgba(34,211,238,.18),transparent_34%),linear-gradient(145deg,#f8fafc,#e8f7fb)]" data-testid="halo-everyday-chapters">
        <div className="lf-container">
          <div className="divide-y divide-ink-300 border-y border-ink-300">
            <article className="grid grid-cols-[2.5rem_1fr] gap-4 py-8">
              <span className="font-mono text-[10px] tracking-[.18em] text-electric">01</span>
              <div><p className="lf-eyebrow">Everyday privacy</p><h2 className="mt-4 font-display text-4xl font-black leading-[.96] md:text-6xl">Public Wi-Fi doesn’t need to feel public.</h2><p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-600">Turn on Halo before you connect at an airport, hotel or café. Your protected connection follows the device instead of depending on the network around you.</p></div>
            </article>
            <article className="grid grid-cols-[2.5rem_1fr] gap-4 py-8">
              <span className="font-mono text-[10px] tracking-[.18em] text-electric">02</span>
              <div><p className="lf-eyebrow">Effortless control</p><h2 className="mt-4 font-display text-4xl font-black leading-[.96] md:text-6xl">One tap. You’re inside Halo.</h2><p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-600">A clear connection state makes protection easy to understand—without turning everyday privacy into a technical project.</p></div>
            </article>
          </div>
        </div>
      </section>

      <section className="lf-editorial-section">
        <div className="lf-container grid gap-14 lg:grid-cols-[.68fr_1.32fr] lg:items-start">
          <div>
            <SectionHeading eyebrow="Halo in everyday life" title="Protection that follows you." body="A premium consumer experience on the surface, backed by the same LightningFlow security and network intelligence underneath." />
          </div>
            <div className="mt-10 divide-y divide-ink-200 border-y border-ink-200">
              {benefits.map(({ icon: Icon, eyebrow, title, body }, index) => (
                <article key={title} className="grid grid-cols-[2rem_1fr] gap-4 py-6">
                  <span className="font-mono text-[10px] tracking-[.18em] text-electric">0{index + 1}</span>
                  <div><Icon className="h-5 w-5 text-electric" strokeWidth={1.6} /><div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{eyebrow}</div><h3 className="mt-2 font-display text-2xl font-bold">{title}</h3><p className="mt-3 leading-relaxed text-ink-600">{body}</p></div>
                </article>
              ))}
            </div>
        </div>
      </section>

      <section id="quantumcloak" className="scroll-mt-24 border-y border-ink-100 bg-[#031124] text-white">
        <div className="lf-container grid gap-12 py-20 md:py-28 lg:max-w-5xl">
          <div>
            <SectionHeading
              light
              eyebrow="Why QuantumCloak"
              title="Three layers. One seamless experience."
              body="Halo SuperVPN brings VPN privacy, LightningFlow’s QuantumCloak security and encrypted traffic together in one remarkably simple experience."
            />
            <div className="mt-8 space-y-3">
              {[
                ["01", "VPN protection", "Privacy for every supported device."],
                ["02", "QuantumCloak security", "Advanced protection working quietly in the background."],
                ["03", "Encrypted traffic", "Your connection, protected as it moves."],
              ].map(([number, title, body]) => (
                <div key={number} className="grid grid-cols-[44px_1fr] gap-4 border-t border-white/15 py-4">
                  <span className="font-mono text-xs text-cyan-200">{number}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-300">{body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                className="lf-btn border border-white bg-white text-ink-900 hover:border-cyan-300 hover:bg-cyan-300"
                to="/quantumcloak"
                data-testid="halo-quantumcloak-learn-more"
              >
                What is QuantumCloak? <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                className="lf-btn border border-white/30 text-white hover:border-white"
                to="/security"
                data-testid="halo-security-trust"
              >
                Security &amp; Trust
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="performance" className="lf-section scroll-mt-24 border-y border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <div className="grid gap-8 lg:grid-cols-[.72fr_1.28fr] lg:items-end">
            <SectionHeading
              eyebrow="Built for speed"
              title="More protection. More speed."
            />
            <p className="max-w-2xl text-lg leading-relaxed text-ink-600">
              Halo SuperVPN delivered up to 25% faster performance—231 Mbps compared with 184 Mbps under the same conditions during comprehensive testing.{" "}
              <Link className="underline underline-offset-4" to="/evidence#halo-speed">See the measured method</Link>.
            </p>
          </div>
          <div className="mt-12">
            <SpeedComparison />
          </div>
        </div>
      </section>

      <section id="plans" className="scroll-mt-24 bg-[#031124] text-white">
        <div className="lf-container py-16 md:py-20">
          <SectionHeading
            eyebrow="Halo SuperVPN availability"
            title="A launch for each qualified platform."
            body="Check each Store below for current availability. LightningFlow Account will bring supported purchases and devices together."
            light
          />

          <div id="store" className="mt-10 grid gap-3 sm:grid-cols-2 lg:max-w-4xl">
            {haloStorefront.stores.map((store) => (
              <StoreAction key={store.id} store={store} dark />
            ))}
          </div>

          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">
            {`Halo SuperVPN is ${HALO_WEBSITE_LIST_PRICE.monthly} per month or ${HALO_WEBSITE_LIST_PRICE.annual} per year, in US dollars. One subscription covers your Mac, iPhone and other supported devices. `}
            {haloStorefront.pricing_published
              ? "Taxes, trial eligibility and renewal terms are displayed before purchase. "
              : "Website purchase is not open yet. "}
            No payment is taken on this page.
          </p>
        </div>

        <div className="border-y border-ink-200 bg-[#f3f3f5] text-ink-900">
          <div className="lf-container grid sm:grid-cols-3">
            {[
              {
                value: HALO_WEBSITE_LIST_PRICE.monthly,
                label: "Per month, US dollars",
              },
              {
                value: HALO_WEBSITE_LIST_PRICE.annual,
                label: "Per year, US dollars",
              },
              {
                value: "Store confirms",
                label: "Introductory offer",
              },
            ].map((item) => (
              <article
                key={item.label}
                className="border-b border-ink-200 px-6 py-7 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 md:px-8"
              >
                <div className="font-display text-3xl font-black tracking-[-.03em]">
                  {item.value}
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[.24em] text-ink-600">
                  {item.label}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <DeviceStory product="halo" showLine={false} />

      <section className="bg-white">
        <div className="lf-container py-12 md:py-16">
          <div className="border border-ink-100 bg-ink-50 p-7 md:flex md:items-center md:justify-between md:gap-10 md:p-9">
            <div>
              <div className="lf-eyebrow">Your Halo account</div>
              <h3 className="mt-3 font-display text-3xl font-black">Your plan. One clear place.</h3>
              <p className="mt-3 max-w-2xl font-semibold leading-relaxed text-ink-900" data-testid="halo-account-line">{DEVICE_STORY_LINE}</p>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-600">
                See your subscription status, renewal date, receipts and payment options with the provider that manages your Halo SuperVPN plan.
              </p>
            </div>
            <Link className="lf-btn-secondary mt-6 shrink-0 md:mt-0" to="/account">
              Account &amp; Billing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section id="privacy" className="border-y border-ink-100 bg-ink-900 text-white">
        <div className="lf-container grid gap-12 py-16 md:py-20 lg:grid-cols-[.72fr_1.28fr]">
          <div>
            <LockKeyhole className="h-7 w-7 text-cyan-300" />
            <h2 className="mt-5 font-display text-4xl font-black">Private by design.</h2>
            <p className="mt-5 leading-relaxed text-slate-200">
              Halo SuperVPN does not retain the sites you visit, your DNS-query history or traffic content. It keeps only the information needed to run your account, protect the service and keep your connection healthy.
            </p>
          </div>
          <div className="grid gap-px bg-white/10 sm:grid-cols-2">
            <article className="bg-ink-900 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">
                Not retained as activity history
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li>Browsing destinations</li>
                <li>DNS-query history</li>
                <li>Traffic content</li>
                <li>Source-IP history</li>
              </ul>
            </article>
            <article className="bg-ink-900 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">
                Minimised service records
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li>Account and subscription</li>
                <li>Billing provider reference</li>
                <li>Security and abuse controls</li>
                <li>Aggregate service health</li>
              </ul>
            </article>
          </div>
          <div className="flex flex-wrap gap-3 lg:col-span-2">
            <Link
              className="lf-btn border border-white/25 text-white hover:border-white"
              to="/halo/privacy"
              data-testid="halo-privacy-link"
            >
              Read Halo SuperVPN privacy
            </Link>
            <Link
              className="lf-btn border border-white/25 text-white hover:border-white"
              to="/security"
              data-testid="halo-security-link"
            >
              Security &amp; Trust
            </Link>
            <Link
              className="lf-btn border border-white/25 text-white hover:border-white"
              to="/halo/support"
              data-testid="halo-support-link"
            >
              Get support
            </Link>
          </div>
        </div>
      </section>

      <section className="lf-section border-b border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <SectionHeading
            eyebrow="Discover QuantumCloak"
            title="Meet the security behind Halo SuperVPN."
            body="Discover the LightningFlow platform built to protect today and evolve for tomorrow."
          />
          <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2">
            {haloEducationArticles.map((article, index) => (
              <article key={article.slug} className="bg-white p-7 md:p-9">
                <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">
                  0{index + 1} · Halo SuperVPN education
                </div>
                <h3 className="mt-5 font-display text-3xl font-black">{article.title}</h3>
                <p className="mt-4 leading-relaxed text-ink-600">{article.summary}</p>
                <Link
                  className="lf-btn-secondary mt-7"
                  to={`/halo/learn/${article.slug}`}
                  data-testid={`halo-education-${article.slug}`}
                >
                  Read article <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-section">
        <div className="lf-container grid gap-12 lg:grid-cols-[.65fr_1.35fr]">
          <SectionHeading eyebrow="Questions" title="Clear before you subscribe." />
          <div className="divide-y divide-ink-100 border border-ink-100 bg-white">
            {faq.map(([question, answer]) => (
              <details key={question} className="group p-6">
                <summary className="cursor-pointer list-none pr-8 font-display text-lg font-bold marker:hidden">
                  {question}
                </summary>
                <p className="mt-4 max-w-3xl leading-relaxed text-ink-600">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-editorial-section lf-editorial-dark" data-testid="halo-final-cta">
        <div className="lf-container grid gap-10 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div><p className="lf-eyebrow text-cyan-200">Halo SuperVPN launch</p><h2 className="mt-5 max-w-5xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">Private protection should feel this simple.</h2><p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">Each Store will show availability, local pricing, terms and any introductory-offer eligibility when its release is ready.</p></div>
          <div className="flex flex-wrap gap-3 lg:justify-end"><a className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:bg-white" href="#plans">See launch availability <ArrowRight className="h-4 w-4" /></a><Link className="lf-btn border border-white/35 text-white hover:border-white" to="/account">LightningFlow Account</Link><Link className="lf-btn border border-white/35 text-white hover:border-white" to="/halo-fortress#compare" data-testid="halo-compare-editions">Compare editions</Link><Link className="lf-btn border border-white/35 text-white hover:border-white" to="/halo-fortress" data-testid="halo-discover-fortress">Discover Fortress</Link></div>
        </div>
      </section>
    </div>
  );
}
